/**
 * FAQ SECTION — Rediseño B2B SaaS Profesional
 * 
 * Estilo coherente con secciones anteriores:
 * - Fondo: #f7f9fb
 * - Colores: #0b1220, #6b7280, #0b63c6
 * - Sin gradientes decorativos
 * - Categorías con border-bottom indicator
 * - Animaciones reveal con Intersection Observer
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  HelpCircle, 
  ChevronDown,
  DollarSign,
  Settings,
  Headphones,
  Calendar,
  Shield,
  Clock,
  CheckCircle2
} from 'lucide-react';

// ─── Intersection Observer Hook ──────────────────────────────────────────────
const useReveal = (delay = 0) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    el.style.transition = `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
};

// ─── FAQItem Component ────────────────────────────────────────────────────────
const FAQItem = ({ pregunta, respuesta, isOpen, onToggle, delay }) => {
  const ref = useReveal(delay);

  return (
    <div
      ref={ref}
      className={`
        bg-white rounded-lg border transition-all duration-300
        ${isOpen
          ? 'border-[#0b63c6] shadow-[0_10px_30px_rgba(11,99,198,0.12)]'
          : 'border-black/[0.08] shadow-[0_4px_12px_rgba(15,23,42,0.03)] hover:border-black/[0.12] hover:shadow-[0_8px_20px_rgba(15,23,42,0.06)]'
        }
      `}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 p-5 text-left"
      >
        <div className="flex items-center gap-3 flex-1">
          <div className={`
            w-6 h-6 rounded-md flex items-center justify-center shrink-0
            ${isOpen ? 'bg-emerald-50' : 'bg-gray-50'}
          `}>
            {isOpen ? (
              <CheckCircle2 size={16} strokeWidth={2} className="text-emerald-600" />
            ) : (
              <HelpCircle size={16} strokeWidth={2} className="text-[#6b7280]" />
            )}
          </div>
          <span className="text-[15px] font-semibold text-[#0b1220] leading-tight">
            {pregunta}
          </span>
        </div>
        <ChevronDown
          size={20}
          strokeWidth={2}
          className={`
            text-[#6b7280] shrink-0 transition-transform duration-300
            ${isOpen ? 'rotate-180 text-[#0b63c6]' : ''}
          `}
        />
      </button>

      <div
        className={`
          overflow-hidden transition-all duration-300
          ${isOpen ? 'max-h-[500px] pb-5' : 'max-h-0'}
        `}
      >
        <div className="px-5">
          <div className="p-4 rounded-lg bg-blue-50 border-l-2 border-[#0b63c6]">
            <p className="text-[14px] text-[#0b1220] leading-[1.7]">
              {respuesta}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [categoriaActiva, setCategoriaActiva] = useState('todas');

  const headerRef = useReveal(0);
  const categoriasRef = useReveal(0);
  const statsRef = useReveal(0);
  const ctaRef = useReveal(0);

  const categorias = [
    { id: 'todas', nombre: 'Todas', icono: <HelpCircle /> },
    { id: 'sistema', nombre: 'El Sistema', icono: <Settings /> },
    { id: 'precio', nombre: 'Precio', icono: <DollarSign /> },
    { id: 'implementacion', nombre: 'Implementación', icono: <Clock /> },
    { id: 'soporte', nombre: 'Soporte', icono: <Headphones /> },
    { id: 'diagnostico', nombre: 'Diagnóstico', icono: <Calendar /> }
  ];

  const faqs = [
    {
      categoria: 'sistema',
      pregunta: "¿El sistema es fácil de usar? No soy muy bueno con tecnología",
      respuesta: "Sí, está diseñado para dueños de empresas de transporte, no para ingenieros. La interfaz es simple y directa: ingresas los datos del viaje (origen, destino, tarifa, gastos) y el sistema hace todos los cálculos. Si sabes usar WhatsApp, podrás usar este sistema sin problema. Además, te capacitamos en 1 hora."
    },
    {
      categoria: 'sistema',
      pregunta: "¿Puedo acceder desde mi celular?",
      respuesta: "Sí, totalmente. El sistema funciona desde cualquier navegador web (Chrome, Safari, etc.) tanto en computadora como en celular o tablet. No necesitas instalar ninguna aplicación. Puedes revisar tus alertas y reportes desde cualquier lugar."
    },
    {
      categoria: 'sistema',
      pregunta: "¿Qué información necesito registrar en el sistema?",
      respuesta: "Para cada viaje registras: origen, destino, cliente, vehículo, chofer, tarifa cobrada, y gastos (combustible, peajes, extras). El sistema calcula automáticamente la rentabilidad. Toma menos de 2 minutos por viaje. También puedes cargar gastos fijos mensuales como mantenimientos, seguros y cuotas de vehículos."
    },
    {
      categoria: 'sistema',
      pregunta: "¿Mis datos están seguros?",
      respuesta: "Sí, completamente. Usamos encriptación de nivel bancario y servidores seguros con respaldos diarios automáticos. Solo tú tienes acceso a tu información. Nunca compartimos ni vendemos datos de nuestros clientes. Cumplimos con todas las normativas de protección de datos."
    },
    {
      categoria: 'precio',
      pregunta: "¿Cuánto cuesta el sistema?",
      respuesta: "El precio depende del número de vehículos y funcionalidades que necesites. En el diagnóstico gratuito analizamos tu caso específico y te damos una propuesta personalizada. Como referencia, empresas con 5 vehículos típicamente invierten entre S/ 300-500 mensuales. La mayoría recupera esta inversión en el primer mes al evitar viajes no rentables."
    },
    {
      categoria: 'precio',
      pregunta: "¿Hay costo de implementación o instalación?",
      respuesta: "No hay costos ocultos ni de instalación. El precio mensual incluye: configuración inicial, capacitación, soporte técnico y todas las actualizaciones. Es un pago único mensual sin sorpresas. Tampoco hay contratos de permanencia mínima."
    },
    {
      categoria: 'precio',
      pregunta: "¿Tienen planes anuales con descuento?",
      respuesta: "Sí, ofrecemos planes anuales con descuentos de 15-20% comparado con el pago mensual. Sin embargo, recomendamos empezar con el plan mensual los primeros 2-3 meses para que compruebes los resultados antes de comprometerte a un año completo."
    },
    {
      categoria: 'precio',
      pregunta: "¿Puedo cancelar cuando quiera?",
      respuesta: "Sí, totalmente. No hay permanencia mínima ni penalidades por cancelación. Si en cualquier momento decides que el sistema no te está sirviendo, simplemente avisas con 30 días de anticipación y listo. Queremos clientes satisfechos, no clientes atrapados en contratos."
    },
    {
      categoria: 'implementacion',
      pregunta: "¿Cuánto tiempo toma implementar el sistema?",
      respuesta: "Entre 3 y 7 días hábiles desde que confirmas. El proceso: (1) Día 1-2: Creamos tu cuenta y cargamos información base (vehículos, choferes, rutas), (2) Día 3-5: Configuras y pruebas con nuestro acompañamiento, (3) Día 6-7: Capacitación final de 1 hora y empiezas a operar. No necesitas parar tu operación ni dedicar tiempo completo."
    },
    {
      categoria: 'implementacion',
      pregunta: "¿Necesito comprar computadoras nuevas o hardware especial?",
      respuesta: "No. El sistema funciona en cualquier computadora, laptop, tablet o celular con conexión a internet. Solo necesitas un navegador web actualizado (Chrome, Firefox, Safari, Edge). No requiere instalación de programas ni hardware adicional."
    },
    {
      categoria: 'implementacion',
      pregunta: "¿Qué pasa con mis datos históricos? ¿Puedo migrarlos?",
      respuesta: "Sí, podemos ayudarte a migrar datos históricos si los tienes en Excel o en otro sistema. Sin embargo, no es obligatorio. Muchos clientes prefieren empezar 'en limpio' y usar el sistema solo para viajes nuevos. Tú decides qué enfoque prefieres según tu caso."
    },
    {
      categoria: 'implementacion',
      pregunta: "¿Necesito contratar a alguien para operarlo?",
      respuesta: "No. El sistema está diseñado para que tú mismo (o la persona que ya lleva tu control actual) lo opere. Registrar un viaje toma 2 minutos. Si ya tienes alguien que lleve tus papeles o Excel, esa misma persona puede usar el sistema sin problema. No requiere conocimientos técnicos avanzados."
    },
    {
      categoria: 'soporte',
      pregunta: "¿Qué tipo de soporte tienen?",
      respuesta: "Soporte directo por WhatsApp en horario de oficina (Lun-Vie 9am-6pm, Sáb 9am-1pm). Respuesta típica en menos de 2 horas. Para problemas urgentes, también ofrecemos videollamadas de soporte. El soporte está incluido en tu mensualidad, sin costos adicionales."
    },
    {
      categoria: 'soporte',
      pregunta: "¿Hacen actualizaciones del sistema?",
      respuesta: "Sí, constantemente. Agregamos nuevas funcionalidades y mejoras basadas en lo que los clientes nos piden. Todas las actualizaciones son automáticas y gratuitas. No tienes que hacer nada, simplemente un día verás nuevas opciones disponibles. Te avisamos por email cuando hay mejoras importantes."
    },
    {
      categoria: 'soporte',
      pregunta: "¿Qué pasa si tengo un problema técnico?",
      respuesta: "Nos escribes por WhatsApp y lo resolvemos. El 90% de consultas se resuelven en menos de 1 hora. Si es algo más complejo, agendamos una videollamada el mismo día o al siguiente. Nunca te dejamos sin respuesta. Además, tenemos respaldos automáticos diarios, así que tu información siempre está segura."
    },
    {
      categoria: 'diagnostico',
      pregunta: "¿El diagnóstico gratuito realmente es gratuito?",
      respuesta: "Sí, 100% gratuito y sin compromiso. Es una videollamada de 15 minutos donde conversamos sobre tu operación, vemos si el sistema tiene sentido para tu caso, y respondemos todas tus dudas. No hay letra chica. Si al final decides que no te interesa, simplemente nos decimos 'gracias' y listo."
    },
    {
      categoria: 'diagnostico',
      pregunta: "¿Qué necesito tener listo para el diagnóstico?",
      respuesta: "Nada especial. Solo información básica que ya conoces: cuántos vehículos tienes, qué tipo de carga transportas, aproximadamente cuántos viajes haces al mes, y si tienes algún control actual (aunque sea en cuaderno). No necesitas números exactos ni preparar documentos. Es una conversación informal."
    },
    {
      categoria: 'diagnostico',
      pregunta: "¿Me van a presionar para comprar en el diagnóstico?",
      respuesta: "No. De hecho, si vemos que el sistema no es adecuado para tu caso (por ejemplo, si tienes solo 1 vehículo o más de 20), te lo diremos con honestidad. Solo enviamos propuesta si tiene sentido real para ti. No ganamos nada teniendo clientes insatisfechos."
    },
    {
      categoria: 'diagnostico',
      pregunta: "¿Puedo ver una demo del sistema antes de contratar?",
      respuesta: "Sí, en el diagnóstico gratuito te mostramos el sistema funcionando en vivo. Ves exactamente cómo se registran viajes, cómo se ven las alertas, cómo funcionan los reportes. No es solo 'te cuento', es 'te muestro'. Así tomas una decisión informada."
    },
    {
      categoria: 'sistema',
      pregunta: "¿Se integra con mi sistema de facturación electrónica?",
      respuesta: "Actualmente no tenemos integración automática con sistemas de facturación. Sin embargo, puedes exportar los datos de viajes a Excel para pasarlos a tu sistema de facturación. Estamos trabajando en integraciones con los principales sistemas peruanos para los próximos meses."
    },
    {
      categoria: 'precio',
      pregunta: "¿Qué formas de pago aceptan?",
      respuesta: "Aceptamos transferencia bancaria, depósito y pago con tarjeta de crédito/débito (Visa, Mastercard). El pago se hace mensualmente por adelantado, típicamente los primeros días del mes. Te enviamos una factura electrónica automáticamente."
    }
  ];

  const faqsFiltrados = categoriaActiva === 'todas' 
    ? faqs 
    : faqs.filter(faq => faq.categoria === categoriaActiva);

  return (
    <section
      id="faq"
      className="w-full py-24 lg:py-28"
      style={{ backgroundColor: '#f7f9fb' }}
    >
      <div className="max-w-[1120px] mx-auto px-6 flex flex-col gap-12">

        {/* ── Header ── */}
        <header ref={headerRef} className="max-w-[720px] mx-auto flex flex-col gap-4 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 mx-auto">
            <HelpCircle size={14} strokeWidth={2} className="text-[#0b63c6]" />
            <span className="text-[13px] font-medium text-[#0b63c6]">Resolvemos tus dudas</span>
          </div>

          <h2 className="text-[28px] font-bold text-[#0b1220] leading-[1.3] tracking-[-0.01em]">
            Preguntas frecuentes
          </h2>

          <p className="text-[15px] text-[#6b7280] leading-[1.6]">
            Todo lo que necesitas saber antes de solicitar tu diagnóstico.{' '}
            <strong className="text-[#0b1220] font-semibold">Si no encuentras tu duda aquí, escríbenos.</strong>
          </p>
        </header>

        {/* ── Categorías ── */}
        <div
          ref={categoriasRef}
          className="flex flex-wrap justify-center gap-2"
        >
          {categorias.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategoriaActiva(cat.id)}
              className={`
                flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13.5px] font-semibold transition-all duration-200
                ${categoriaActiva === cat.id
                  ? 'bg-[#0b63c6] text-white shadow-[0_4px_12px_rgba(11,99,198,0.3)]'
                  : 'bg-white text-[#6b7280] border border-black/[0.08] hover:border-[#0b63c6]/40 hover:text-[#0b1220]'
                }
              `}
            >
              {React.cloneElement(cat.icono, { 
                size: 16, 
                strokeWidth: 2 
              })}
              <span>{cat.nombre}</span>
            </button>
          ))}
        </div>

        {/* ── FAQs List ── */}
        <div className="max-w-[900px] mx-auto w-full flex flex-col gap-3">
          {faqsFiltrados.map((faq, i) => (
            <FAQItem
              key={i}
              {...faq}
              isOpen={activeIndex === i}
              onToggle={() => setActiveIndex(activeIndex === i ? null : i)}
              delay={i * 30}
            />
          ))}
        </div>

        {/* ── Stats ── */}
        <div
          ref={statsRef}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-6 bg-white rounded-xl border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.04)]"
        >
          {[
            { icon: <Shield />, numero: "100%", label: "Sin compromiso" },
            { icon: <Clock />, numero: "< 24h", label: "Tiempo de respuesta" },
            { icon: <Headphones />, numero: "6 días", label: "Soporte activo" },
            { icon: <DollarSign />, numero: "S/ 0", label: "Diagnóstico gratuito" }
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                {React.cloneElement(stat.icon, {
                  size: 24,
                  strokeWidth: 1.75,
                  className: 'text-[#0b63c6]'
                })}
              </div>
              <div>
                <div className="text-[20px] font-bold text-[#0b63c6] leading-tight">
                  {stat.numero}
                </div>
                <div className="text-[12px] text-[#6b7280] font-medium">
                  {stat.label}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── CTA Final ── */}
        <div
          ref={ctaRef}
          className="flex flex-col items-center gap-5 p-8 rounded-xl bg-gradient-to-r from-[#0b1220] to-[#1e293b] border border-white/10 text-center"
        >
          <div>
            <h3 className="text-[20px] font-bold text-white leading-tight mb-2">
              ¿Aún tienes dudas?
            </h3>
            <p className="text-[14px] text-white/80 leading-[1.6]">
              Agenda tu diagnóstico gratuito y las resolvemos todas en la videollamada.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {[
              "15 minutos de tu tiempo",
              "Sin compromiso de compra",
              "Respuestas honestas"
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20"
              >
                <CheckCircle2 size={16} strokeWidth={2} className="text-emerald-400" />
                <span className="text-[13px] text-white/90 font-medium">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default FAQ;